import * as Phaser from 'phaser'
import { Color } from './color'
import { Point } from '../math/point'
import { normalizeAngle, Angle } from '../math/angle'
import { LineStyle } from './lines'
import { Transform } from 'src/app/math/transform'
import { ViewTransform, Viewport } from '../game/view-transform'
import { Line, hLineByMidpoint, vLineByMidpoint } from '../math/line'

function convertAngleToScreen(angle: number): number {
  const angle0 = -normalizeAngle(angle)
  return angle0
}

/**
 * Draw primatives in transformed coordinates
 */
export class Graphics {
  protected readonly transform = new Transform()
  relative = true

  constructor(
    readonly worldViewport: () => Viewport,
    readonly viewTransform: ViewTransform,
    readonly phaserGraphics: Phaser.GameObjects.Graphics,
    readonly sound: Phaser.Sound.BaseSoundManager,
    readonly make: Phaser.GameObjects.GameObjectCreator
    ) {}

  convert(p: Point): Point {
    const m = this.viewTransform.worldToScreen(this.worldViewport(), this.relative)
    return this.transform.convert(p, m)
  }

  // assumes x-axis and y-axis have same scale
  convertScalarToScreen(scalar: number): number {
    return this.viewTransform.worldToScreenScalar(scalar, this.worldViewport())
  }

  clear() {
    this.phaserGraphics.clear()
  }

  play(key: string) {
    this.sound.play(key)
  }

  lineStyle(l: LineStyle) {
    this.phaserGraphics.lineStyle(l.lineWidth, l.c.getColor(), l.c.alpha)
  }

  fillStyle(c: Color) {
    this.phaserGraphics.fillStyle(c.getColor(), c.alpha)
  }

  protected _point(p0: Point, radius: number) {
    this.phaserGraphics.fillPoint(p0.x, p0.y, radius)
  }

  point(p0: Point, radius: number = 1) {
    const s0 = this.convert(p0)
    const r0 = this.convertScalarToScreen(radius)

    this._point(s0, r0)
  }

  protected _circle(p0: Point, radius: number) {
    this.phaserGraphics.fillCircle(p0.x, p0.y, radius)
  }

  circle(p0: Point, radius: number = 1) {
    const s0 = this.convert(p0)
    const r0 = this.convertScalarToScreen(radius)

    this._circle(s0, r0)
  }

  protected _line(p0: Point, p1: Point) {
    this.phaserGraphics.beginPath()
    this.phaserGraphics.moveTo(p0.x, p0.y)
    this.phaserGraphics.lineTo(p1.x, p1.y)
    this.phaserGraphics.strokePath()
  }

  line(p0: Point, p1: Point) {
    const s0 = this.convert(p0)
    const s1 = this.convert(p1)

    this._line(s0, s1)
  }

  square(p: Point, l: number = 10) {
    const p0 = this.convert(p)
    const l0 = this.convertScalarToScreen(l)/2

    this.phaserGraphics.fillRect(p0.x-l0, p0.y-l0, l0*2, l0*2)
  }

  drawLine(l: Line) {
    const s0 = this.convert(l.p0)
    const s1 = this.convert(l.p1)

    this._line(s0, s1)
  }

  drawHighlight(p: Point, len: number = 5) {
    const p0 = p //this.convert(p)

    // draw cursor highlight
    const hLine1 = hLineByMidpoint(new Point(p0.x, p0.y-len), len)
    const hLine2 = hLineByMidpoint(new Point(p0.x, p0.y+len), len)
    const vLine1 = vLineByMidpoint(new Point(p0.x-len, p0.y), len)
    const vLine2 = vLineByMidpoint(new Point(p0.x+len, p0.y), len)
    const style = new LineStyle(new Color(0x00, 0x00, 0xFF), 2)
    this.lineStyle(style)
    this.drawLine(hLine1)
    this.drawLine(hLine2)
    this.drawLine(vLine1)
    this.drawLine(vLine2)
  }

  protected _arc(p: Point, r: number, angle0: number, angle1: number) {
    this.phaserGraphics.beginPath()
    this.phaserGraphics.arc(p.x, p.y, r, angle0, angle1, false)
    this.phaserGraphics.strokePath()
  }

  arc(p: Point, r: number, angle0: number, angle1: number) {
    const p0 = this.convert(p)
    const r0 = this.convertScalarToScreen(r)
    const theta0 = convertAngleToScreen(angle0)
    const theta1 = convertAngleToScreen(angle1)

    this._arc(p0, r0, theta0, theta1)
  }

  protected _slice(p: Point, r: number, center: number, angle: number) {
    this.phaserGraphics.slice(p.x, p.y, r, center - angle, center + angle, false)
    this.phaserGraphics.fillPath();
  }

  slice(p: Point, r: number, center: number, angle: number) {
    const p0 = this.convert(p)
    const r0 = this.convertScalarToScreen(r)
    const center0 = convertAngleToScreen(center)

    this._slice(p0, r0, center0, angle)
  }

  protected moveTo(p: Point) {
    const p0 = this.convert(p)
    this.phaserGraphics.moveTo(p0.x, p0.y)
  }

  protected lineTo(p: Point) {
    const p0 = this.convert(p)
    this.phaserGraphics.lineTo(p0.x, p0.y)
  }

  image(image: Phaser.GameObjects.Image, p: Point, rotation: number) {
    const p0 = this.convert(p)
    const rotation0 = convertAngleToScreen(rotation)

    // TODO: fix angle conversion
    image.setRotation(rotation0+Angle.PI_2)
    image.setPosition(p0.x, p0.y)
  }
}
